import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

import { PackageAssignment } from '@/components/catalog/package-assignment';
import { useDetailTitle } from '@/components/page-header';
import { EmptyState, SectionCard } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { getMetrcPackage, type MetrcPackage } from '@/lib/admin-api';
import { useAuth } from '@/lib/auth';

/**
 * One Metrc package: the state's record of a physical lot, and what we sell it
 * as.
 *
 * The Metrc facts are read-only and deliberately so -- they mirror what the
 * regulator holds, and the way to change one is to move the lot in Metrc and
 * let the sync bring the change back. The ASSIGNMENT is ours: which variant
 * this lot is sold as, what it cost, when it expires. That is the one thing
 * this screen can change, and doing so moves stock.
 *
 * @see docs/metrc.md
 */
export const Route = createFileRoute('/packages/$packageId')({
  component: PackageDetailPage,
});

function PackageDetailPage() {
  const { packageId } = useParams({ from: '/packages/$packageId' });
  const { activeAccountId } = useAuth();
  const accountId = activeAccountId ?? '';

  const query = useQuery({
    queryKey: ['metrc-packages', accountId, packageId],
    queryFn: () => getMetrcPackage(accountId, packageId),
    enabled: Boolean(accountId),
  });

  const pkg = query.data ?? null;

  useDetailTitle(pkg?.label ?? 'Package');

  if (query.isLoading) return <p className="text-muted-foreground">Loading…</p>;

  if (!pkg) {
    return (
      <EmptyState>
        No such package.{' '}
        <Link to="/packages" className="underline">
          Back to packages
        </Link>
      </EmptyState>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-xl font-semibold tracking-tight">{pkg.label}</h1>
        {pkg.listType && <Badge variant="secondary">{pkg.listType}</Badge>}
        <Badge variant={pkg.assignment ? 'default' : 'outline'}>
          {pkg.assignment ? 'Assigned' : 'Unassigned'}
        </Badge>
      </div>

      <PackageAssignment
        accountId={accountId}
        pkg={pkg}
        onChanged={(next: MetrcPackage) => query.refetch().then(() => next)}
      />

      <SectionCard
        title="What Metrc holds"
        description="Mirrored from the state. Move the lot in Metrc and the sync brings the change back — editing this copy would only make the two disagree."
      >
        <dl className="space-y-2 text-sm">
          <Fact label="Item" value={pkg.itemName ?? '—'} />
          <Fact label="Category" value={pkg.itemCategory ?? '—'} />
          <Fact label="Strain" value={pkg.strainName ?? '—'} />
          <Fact
            label="Quantity"
            value={`${pkg.quantity ?? 0} ${pkg.unitOfMeasureName ?? ''}`.trim()}
          />
          <Fact label="Lab testing" value={pkg.labTestingState ?? '—'} />
          <Fact label="Licence" value={pkg.licenseNumber} />
          <Fact label="Packaged" value={formatDate(pkg.packagedDate)} />
          <Fact label="Expires" value={formatDate(pkg.expirationDate)} />
        </dl>
      </SectionCard>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';

  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(value));
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
